document.addEventListener('DOMContentLoaded', () => {
	gsap.registerPlugin(ScrollTrigger);
	const slider = document.querySelector('.photo-slider');
	if (!slider) return;

	const slides = gsap.utils.toArray('.photo-slide');
	if (!slides.length) return;

	// GSAP's horizontalLoop helper function
	function horizontalLoop(items, config) {
		items = gsap.utils.toArray(items);
		config = config || {};
		let tl = gsap.timeline({
				repeat: config.repeat,
				paused: config.paused,
				defaults: { ease: 'none' }, // GPU acceleration
				onReverseComplete: () =>
					tl.totalTime(tl.rawTime() + tl.duration() * 100),
			}),
			length = items.length,
			startX = items[0].offsetLeft,
			times = [],
			widths = [],
			xPercents = [],
			curIndex = 0,
			pixelsPerSecond = (config.speed || 1) * 100,
			snap =
				config.snap === false
					? (v) => v
					: gsap.utils.snap(config.snap || 1),
			totalWidth,
			curX,
			distanceToStart,
			distanceToLoop,
			item,
			i;

		gsap.set(items, {
			xPercent: (i, el) => {
				let w = (widths[i] = parseFloat(
					gsap.getProperty(el, 'width', 'px')
				));
				xPercents[i] = snap(
					(parseFloat(gsap.getProperty(el, 'x', 'px')) / w) * 100 +
						gsap.getProperty(el, 'xPercent')
				);
				return xPercents[i];
			},
		});

		gsap.set(items, { x: 0 }); // GPU acceleration

		// Calculate total width including gaps
		const gapSize = parseFloat(config.paddingRight) || 0;
		totalWidth =
			items[length - 1].offsetLeft +
			(xPercents[length - 1] / 100) * widths[length - 1] -
			startX +
			items[length - 1].offsetWidth *
				gsap.getProperty(items[length - 1], 'scaleX') +
			gapSize;

		for (i = 0; i < length; i++) {
			item = items[i];
			curX = (xPercents[i] / 100) * widths[i];
			distanceToStart = item.offsetLeft + curX - startX;
			distanceToLoop =
				distanceToStart + widths[i] * gsap.getProperty(item, 'scaleX');
			tl.to(
				item,
				{
					xPercent: snap(((curX - distanceToLoop) / widths[i]) * 100),
					duration: distanceToLoop / pixelsPerSecond,
				},
				0
			)
				.fromTo(
					item,
					{
						xPercent: snap(
							((curX - distanceToLoop + totalWidth) / widths[i]) *
								100
						),
					},
					{
						xPercent: xPercents[i],
						duration:
							(curX - distanceToLoop + totalWidth - curX) /
							pixelsPerSecond,
						immediateRender: false,
					},
					distanceToLoop / pixelsPerSecond
				)
				.add('label' + i, distanceToStart / pixelsPerSecond);
			times[i] = distanceToStart / pixelsPerSecond;
		}

		function toIndex(index, vars) {
			vars = vars || {};
			Math.abs(index - curIndex) > length / 2 &&
				(index += index > curIndex ? -length : length);
			let newIndex = gsap.utils.wrap(0, length, index),
				time = times[newIndex];
			if (time > tl.time() !== index > curIndex) {
				vars.modifiers = { time: gsap.utils.wrap(0, tl.duration()) };
				time += tl.duration() * (index > curIndex ? 1 : -1);
			}
			curIndex = newIndex;
			vars.overwrite = true;
			return tl.tweenTo(time, vars);
		}

		tl.next = (vars) => toIndex(curIndex + 1, vars);
		tl.previous = (vars) => toIndex(curIndex - 1, vars);
		tl.current = () => curIndex;
		tl.toIndex = (index, vars) => toIndex(index, vars);
		tl.times = times;
		tl.progress(1, true).progress(0, true);

		if (config.reversed) {
			tl.vars.onReverseComplete();
			tl.reverse();
		}

		return tl;
	}

	// Resolves once all <img> elements inside the slider have finished loading,
	// or after `ms` milliseconds as a safety net (for broken/never-loading images).
	function waitForImages(ms = 3000) {
		const imgs = Array.from(slider.querySelectorAll('img'));
		if (!imgs.length) return Promise.resolve();

		const loaded = Promise.all(
			imgs.map(
				(img) =>
					new Promise((resolve) => {
						if (img.complete && img.naturalWidth !== 0) {
							resolve();
						} else {
							img.addEventListener('load', resolve, {
								once: true,
							});
							img.addEventListener('error', resolve, {
								once: true,
							});
						}
					})
			)
		);

		return Promise.race([loaded, new Promise((r) => setTimeout(r, ms))]);
	}

	function initLoop() {
		const wrapper = slider.querySelector('.slides-wrapper');
		const gap = parseFloat(getComputedStyle(wrapper).gap) || 0;

		const loop = horizontalLoop(slides, {
			repeat: -1,
			speed: 1,
			paddingRight: gap,
		});

		let speedTween;
		ScrollTrigger.create({
			trigger: slider,
			start: 'top bottom',
			end: 'bottom top',
			onLeave: () => loop.pause(),
			onLeaveBack: () => loop.pause(),
			onEnter: () => loop.play(),
			onEnterBack: () => loop.play(),
			onUpdate(self) {
				if (speedTween) speedTween.kill();
				speedTween = gsap
					.timeline()
					.to(loop, { timeScale: 3 * self.direction, duration: 0.25 })
					.to(
						loop,
						{ timeScale: 1 * self.direction, duration: 1.5 },
						'+=0.4'
					);
			},
		});
	}

	const observer = new IntersectionObserver(
		(entries, obs) => {
			if (!entries[0].isIntersecting) return;
			obs.disconnect();

			slider.querySelectorAll('img').forEach((img) => {
				if (img.loading === 'lazy') {
					img.loading = 'eager';
					if (!img.complete) {
						const src = img.src;
						img.src = '';
						img.src = src;
					}
				}
			});

			// ✅ Double rAF ensures images have reflowed before GSAP measures dimensions
			waitForImages().then(() => {
				requestAnimationFrame(() => {
					requestAnimationFrame(initLoop);
				});
			});
		},
		{ threshold: 0 }
	);

	observer.observe(slider);
});
